import { action, makeObservable, observable } from 'mobx';
import { socialAuthError, socialAuthSuccess } from '@wix/bi-logger-hls2/v2';
import {
  StateMachineResponse,
  StateType,
} from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { RootStore } from './root';
import { openIdpPopup } from '../utils/openIdpPopup';
import {
  ERROR_CODES,
  extractServerErrorCode,
  extractRedirectUrl,
} from '../utils/errorHandler';
import { handleSsoRedirect } from '../utils/handleSsoRedirect';
import {
  IDP_POPUP_BI_VERSION,
  idpPopupOpenInteraction,
  providerIdFromIdpConnectionId,
  TWOFA_PROCESS,
} from '../utils/constants';
import { ROUTES } from '../routes';
import { AUTH_TYPE } from '../components/BlockedAccount/authTypes';
import { createBiLogger } from '../services/biLogger';

const REQUIRE_FIRST_FACTOR_STATE = 'REQUIRE_FIRST_FACTOR';

function extractEmailFromIdpResponse(
  response: StateMachineResponse,
): string | undefined {
  // IAM v1 standard identity email
  if (response.identity?.email?.address) {
    return response.identity.email.address;
  }
  // Legacy identity profile format (multiple emails array)
  if (response.identity?.identityProfile?.emails?.[0]) {
    return response.identity.identityProfile.emails[0];
  }
  // CommonCustomValue in additionalData (some IDP providers return email here)
  if (response.additionalData?.email?.strValue) {
    return response.additionalData.email.strValue;
  }
  return undefined;
}

// The IDP popup may return state as a structured object rather than a plain StateType enum value
interface IamStateObject {
  status?: string;
  stateToken?: string;
}

/**
 * Store for handling IDP (Identity Provider) connection login flow.
 * Similar to social login but uses the IDP popup flow for authentication.
 */
export class IdpConnectionLoginStore {
  protected readonly rootStore: RootStore;
  isLoading: boolean = false;
  hasError: boolean = false;
  errorCode: string = '';
  isIdpFirstFactorFlow: boolean = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      isLoading: observable,
      hasError: observable,
      errorCode: observable,
      isIdpFirstFactorFlow: observable,
      loginWithIdpConnection: action.bound,
      handleError: action.bound,
      clearErrors: action.bound,
    });
  }

  /**
   * Initiates login via an Identity Provider connection.
   *
   * @param idpConnectionId - The unique identifier of the IDP connection
   * @param ldSessionId - The login dialog session ID for tracking
   * @returns Promise resolving to login result with success status
   */
  async loginWithIdpConnection(
    idpConnectionId: string,
    ldSessionId: string,
  ): Promise<{ success: boolean; errorCode?: string }> {
    this.isLoading = true;
    this.hasError = false;
    this.errorCode = '';

    const popupOpenInteraction = idpPopupOpenInteraction(
      providerIdFromIdpConnectionId(idpConnectionId),
    );
    this.rootStore.panorama.startTransaction(popupOpenInteraction);

    try {
      const loginResponse = await openIdpPopup({
        idpConnectionId,
        ldSessionId,
        extraCustomPayload:
          this.rootStore.apiStore.buildIdpCustomPayloadExtras(),
        onPopupOpened: () =>
          this.rootStore.panorama.finishTransaction(popupOpenInteraction),
      });

      return await this.handleLoginSuccess(loginResponse, idpConnectionId);
    } catch (error: any) {
      return await this.handleLoginError(error, idpConnectionId);
    } finally {
      this.isLoading = false;
    }
  }

  private async handleLoginSuccess(
    loginResponse: StateMachineResponse,
    idpConnectionId: string,
  ): Promise<{ success: boolean }> {
    const stateObj = loginResponse.state as unknown as IamStateObject;
    const stateValue =
      stateObj?.status ?? (loginResponse.state as unknown as string);

    if (stateValue === REQUIRE_FIRST_FACTOR_STATE) {
      return this.handleFirstFactorRequired(loginResponse, idpConnectionId);
    }

    // Check for MFA challenge
    if (stateValue === StateType.MFA_CHALLENGE && loginResponse.stateToken) {
      this.rootStore.panorama.startTransaction(TWOFA_PROCESS);
      this.rootStore.twoFactorAuthStore.setAuthParamsMfaIAM(loginResponse);
      this.rootStore.navigationStore.navigate(ROUTES.TWO_FACTOR_AUTH);
      return { success: false };
    }

    // Only proceed to success if state is SUCCESS or DONE
    const isSuccessState =
      stateValue === StateType.SUCCESS ||
      stateValue === 'SUCCESS' ||
      stateValue === 'DONE';
    if (!isSuccessState) {
      const reason = `Unexpected state: ${stateValue}`;
      this.rootStore.biLogger.report(
        socialAuthError({
          socialNetwork: providerIdFromIdpConnectionId(idpConnectionId),
          reason,
          version: IDP_POPUP_BI_VERSION,
        }),
      );
      this.handleError(reason);
      return { success: false };
    }

    this.rootStore.biLogger = createBiLogger();
    this.rootStore.postLoginStore.biLogger = createBiLogger();

    this.rootStore.biLogger.report(
      socialAuthSuccess({
        socialNetwork: providerIdFromIdpConnectionId(idpConnectionId),
        context: this.rootStore.emailStepStore.getContext(),
        version: IDP_POPUP_BI_VERSION,
      }),
    );

    const additionalData = loginResponse.additionalData ?? {};
    const postAuthParams = {
      uaToken: additionalData.uaToken?.strValue,
      authMethod: additionalData.authMethod?.strValue,
    };
    if (additionalData.isNewIdentity?.numValue === 1) {
      this.rootStore.navigationStore.postSignup(postAuthParams);
    } else {
      this.rootStore.navigationStore.postLogin(postAuthParams);
    }

    return { success: true };
  }

  private handleFirstFactorRequired(
    response: StateMachineResponse,
    idpConnectionId: string,
  ): { success: boolean } {
    const email = extractEmailFromIdpResponse(response);

    if (email) {
      this.rootStore.userDataStore.setEmail(email);
    }

    const stateObj = response.state as unknown as IamStateObject;
    const stateToken = stateObj?.stateToken ?? response.stateToken ?? undefined;
    this.rootStore.loginStore.setStateToken(
      stateToken,
      this.rootStore.userDataStore.email,
    );

    this.isIdpFirstFactorFlow = true;
    this.rootStore.setActiveSocialProvider(
      providerIdFromIdpConnectionId(idpConnectionId),
    );

    this.rootStore.notificationStore.openWarningToast(
      this.rootStore.i18n.t('account_verification.social_connect_error', {
        email: this.rootStore.userDataStore.email,
      }),
    );
    this.rootStore.emailStepStore.emailField.setValue(
      this.rootStore.userDataStore.email,
    );
    this.rootStore.emailStepStore.submit(true);

    return { success: false };
  }

  private async handleLoginError(
    error: any,
    idpConnectionId: string,
  ): Promise<{ success: boolean; errorCode?: string }> {
    const errorCode = extractServerErrorCode(error);

    this.rootStore.biLogger.report(
      socialAuthError({
        socialNetwork: providerIdFromIdpConnectionId(idpConnectionId),
        reason: errorCode || error?.message || 'Unknown error',
        version: IDP_POPUP_BI_VERSION,
      }),
    );

    // Handle specific error cases
    if (
      errorCode === ERROR_CODES.USER_IS_BLOCKED ||
      errorCode === ERROR_CODES.ACCOUNT_NOT_FOUND
    ) {
      this.rootStore.navigationStore.navigateToBlockedAccount({
        refferal_info: AUTH_TYPE.SOCIAL,
      });
      return { success: false, errorCode };
    }

    if (errorCode === ERROR_CODES.SSO_LOGIN_MANDATORY_ERROR) {
      // User has SSO connection - redirect to SSO login
      const redirectUrl = extractRedirectUrl(error);
      if (redirectUrl) {
        this.rootStore.navigationStore.redirect(redirectUrl);
        return { success: false, errorCode };
      }
    }

    if (errorCode === ERROR_CODES.SOCIAL_LOGIN_GET_EMAIL) {
      // User needs to provide email - navigate to social enter email page
      this.rootStore.setActiveSocialProvider(
        providerIdFromIdpConnectionId(idpConnectionId),
      );
      this.rootStore.navigationStore.navigate(ROUTES.SOCIAL_ENTER_EMAIL);
      return { success: false, errorCode };
    }

    if (errorCode === ERROR_CODES.NO_PERSONAL_WIX_ACCOUNT) {
      // The new IAM system returns "NO_PERSONAL_WIX_ACCOUNT" when the social
      // identity authenticated but there is no matching personal Wix account.
      // The popup forwards the account email in errorData.
      const email =
        error.errorData?.email || this.rootStore.userDataStore.email;
      // Without an email we cannot run the SSO check (userAccountsByEmail), so
      // fall through to the generic error below.
      if (email) {
        this.rootStore.userDataStore.setEmail(email);
        this.rootStore.setActiveSocialProvider(
          providerIdFromIdpConnectionId(idpConnectionId),
        );
        // Redirect to SSO if the account mandates it; otherwise there is no
        // account to verify, so fall through to the generic error below.
        const ssoRedirected = await handleSsoRedirect(this.rootStore, email);
        if (ssoRedirected) {
          return { success: false, errorCode };
        }
      }
    }

    // Generic error handling
    this.hasError = true;
    this.errorCode = errorCode || 'UNKNOWN_ERROR';

    return { success: false, errorCode: this.errorCode };
  }

  handleError(error?: string) {
    this.hasError = true;
    this.errorCode = error || 'UNKNOWN_ERROR';
  }

  clearErrors() {
    this.hasError = false;
    this.errorCode = '';
  }
}
